import { buildSiteCatalog, resolveCanonicalSiteIdentity } from './siteIdentity';

const toArray = (value) => (Array.isArray(value) ? value : []);

const normalize = (value) => String(value || '').trim().toLowerCase();

const customerIdOf = (customer = {}) => String(customer.id || customer.customer_id || '').trim();
const customerCodeOf = (customer = {}) => String(customer.customer_id || customer.id || '-').trim();
const customerNameOf = (customer = {}) => String(customer.name || customer.customer_name || customerCodeOf(customer) || '-').trim();
const customerStatusRaw = (customer = {}) => String(customer.status || customer.connection_status || customer.monitoring_status || customer.status_detail?.connection_status || 'unknown').trim();
const customerSiteOf = (customer = {}, siteCatalog = null) => {
  const resolved = resolveCanonicalSiteIdentity(customer, siteCatalog);
  return String(resolved.site_id || '-').trim();
};
const linkedOdpIdOf = (customer = {}) => String(customer.linked_odp_id || customer.odp_id || '').trim();
const linkedOltIdOf = (customer = {}) => String(customer.linked_olt_id || customer.olt_id || '').trim();
const linkedRouterIdOf = (customer = {}) => String(customer.router_id || customer.nas_id || '').trim();

const classifyCustomerState = (statusValue) => {
  const status = normalize(statusValue);
  if (!status) return 'unknown';
  if (['offline', 'down', 'terminated', 'critical', 'error'].includes(status)) return 'offline';
  if (['degraded', 'unstable', 'suspended'].includes(status)) return 'degraded';
  if (['warning', 'warn', 'maintenance', 'pending'].includes(status)) return 'warning';
  if (['online', 'active', 'ok', 'healthy'].includes(status)) return 'healthy';
  return 'unknown';
};

const buildIndexes = ({ customers = [], odps = [], olts = [], siteCatalog = null }) => {
  const customersById = new Map();
  const customersByOdp = new Map();
  const customersByOlt = new Map();
  const customersByRouter = new Map();
  const customersBySite = new Map();

  const addMapList = (map, key, value) => {
    const token = String(key || '').trim();
    if (!token) return;
    if (!map.has(token)) map.set(token, []);
    map.get(token).push(value);
  };

  customers.forEach((customer) => {
    const id = customerIdOf(customer);
    if (!id) return;
    customersById.set(id, customer);
    addMapList(customersByOdp, linkedOdpIdOf(customer), customer);
    addMapList(customersByOlt, linkedOltIdOf(customer), customer);
    addMapList(customersByRouter, linkedRouterIdOf(customer), customer);
    addMapList(customersBySite, customerSiteOf(customer, siteCatalog), customer);
  });

  const oltById = new Map();
  const oltIdsByRouterId = new Map();
  const addOltByRouter = (routerId, oltId) => {
    const rid = String(routerId || '').trim();
    const oid = String(oltId || '').trim();
    if (!rid || !oid) return;
    if (!oltIdsByRouterId.has(rid)) oltIdsByRouterId.set(rid, new Set());
    oltIdsByRouterId.get(rid).add(oid);
  };

  olts.forEach((olt) => {
    const oid = String(olt.id || olt.olt_id || '').trim();
    if (!oid) return;
    oltById.set(oid, olt);
    addOltByRouter(olt.router_id, oid);
    addOltByRouter(olt.uplink_router_id, oid);
  });

  const odpById = new Map();
  const odpIdsByOltId = new Map();
  odps.forEach((odp) => {
    const oid = String(odp.id || odp.odp_id || '').trim();
    if (!oid) return;
    odpById.set(oid, odp);
    const oltId = String(odp.olt_id || '').trim();
    if (!oltId) return;
    if (!odpIdsByOltId.has(oltId)) odpIdsByOltId.set(oltId, new Set());
    odpIdsByOltId.get(oltId).add(oid);
  });

  return {
    customersById,
    customersByOdp,
    customersByOlt,
    customersByRouter,
    customersBySite,
    oltById,
    oltIdsByRouterId,
    odpById,
    odpIdsByOltId,
  };
};

const collectCustomersByNode = ({ contextType, contextId, indexes }) => {
  const id = String(contextId || '').trim();
  if (!id) return new Set();

  const {
    customersById,
    customersByOdp,
    customersByOlt,
    customersByRouter,
    customersBySite,
    oltIdsByRouterId,
    odpIdsByOltId,
  } = indexes;

  const collected = new Set();
  const addCustomer = (customer) => {
    const cid = customerIdOf(customer);
    if (cid) collected.add(cid);
  };
  const addCustomers = (items = []) => items.forEach(addCustomer);

  const normalizedType = normalize(contextType);

  if (normalizedType === 'customer') {
    if (customersById.has(id)) collected.add(id);
    return collected;
  }

  if (normalizedType === 'odp') {
    addCustomers(customersByOdp.get(id));
    return collected;
  }

  if (normalizedType === 'olt') {
    addCustomers(customersByOlt.get(id));
    const odpIds = odpIdsByOltId.get(id);
    if (odpIds) {
      Array.from(odpIds).forEach((odpId) => addCustomers(customersByOdp.get(odpId)));
    }
    return collected;
  }

  if (normalizedType === 'nas' || normalizedType === 'router' || normalizedType === 'node') {
    addCustomers(customersByRouter.get(id));
    const oltIds = oltIdsByRouterId.get(id);
    if (oltIds) {
      Array.from(oltIds).forEach((oltId) => {
        addCustomers(customersByOlt.get(oltId));
        const odpIds = odpIdsByOltId.get(oltId);
        if (odpIds) {
          Array.from(odpIds).forEach((odpId) => addCustomers(customersByOdp.get(odpId)));
        }
      });
    }
    return collected;
  }

  if (normalizedType === 'site') {
    addCustomers(customersBySite.get(id));
    return collected;
  }

  return collected;
};

const collectCustomersFromIncident = ({ incident, indexes }) => {
  const ids = new Set();
  if (!incident) return ids;

  const addIds = (set) => {
    Array.from(set || []).forEach((id) => {
      const token = String(id || '').trim();
      if (token) ids.add(token);
    });
  };

  toArray(incident.affectedCustomers).forEach((id) => {
    const token = String(id || '').trim();
    if (token) ids.add(token);
  });

  const refs = toArray(incident.impactedEntities || incident.linkedEntityKeys);
  refs.forEach((ref) => {
    const [rawType, rawId] = String(ref || '').split(':');
    const type = normalize(rawType);
    const id = String(rawId || '').trim();
    if (!type || !id) return;
    addIds(collectCustomersByNode({ contextType: type, contextId: id, indexes }));
  });

  return ids;
};

const deriveSeverity = ({ total, offline, degraded, warning }) => {
  if (offline >= 10 || total >= 25) return 'critical';
  if (offline > 0 || degraded >= 8 || total >= 12) return 'high';
  if (degraded > 0 || warning > 0 || total >= 3) return 'medium';
  return 'low';
};

const severityLabel = (token) => {
  if (token === 'critical') return 'critical';
  if (token === 'high') return 'high';
  if (token === 'medium') return 'medium';
  return 'low';
};

const severityTone = (token) => {
  if (token === 'critical') return 'danger';
  if (token === 'high') return 'warn';
  if (token === 'medium') return 'info';
  return 'neutral';
};

const relationMitigationHint = (relations = []) => {
  const items = toArray(relations);
  if (!items.length) return { backupAvailable: false, balancingAvailable: false, hint: '' };

  const hasBackup = items.some((relation) => {
    const type = normalize(relation?.relation_type);
    if (type !== 'backup') return false;
    if (relation?.is_active === false) return false;
    const linkStatus = normalize(relation?.link_status);
    return ['active', 'idle', 'degraded', 'unknown', 'ready'].includes(linkStatus) || !linkStatus;
  });

  const hasBalancing = items.some((relation) => {
    const type = normalize(relation?.relation_type);
    if (type !== 'balancing') return false;
    if (relation?.is_active === false) return false;
    const usage = normalize(relation?.path_usage);
    return usage === 'balancing_active' || usage === 'balancing_ready' || !usage;
  });

  if (hasBackup) {
    return {
      backupAvailable: true,
      balancingAvailable: hasBalancing,
      hint: 'Backup path available. Mitigasi mungkin mengurangi dampak.',
    };
  }

  if (hasBalancing) {
    return {
      backupAvailable: false,
      balancingAvailable: true,
      hint: 'Balancing path available. Mitigasi dapat membantu distribusi dampak.',
    };
  }

  return { backupAvailable: false, balancingAvailable: false, hint: '' };
};

export const customerImpactStateMessage = {
  unavailable: 'Dampak pelanggan belum bisa dihitung penuh.',
  empty: 'Belum ada customer impact yang terdeteksi.',
  partial: 'Relasi pelanggan belum lengkap untuk konteks ini.',
};

export const deriveCustomerImpactSummary = ({
  context = {},
  data = {},
  relations = [],
}) => {
  const customers = toArray(data.customers);
  const odps = toArray(data.odps);
  const olts = toArray(data.olts);
  const sites = toArray(data.sites);
  const incidents = toArray(data.incidents);
  const siteCatalog = buildSiteCatalog(sites);

  if (!customers.length) {
    return {
      state: 'unavailable',
      message: customerImpactStateMessage.unavailable,
      counts: { total: 0, offline: 0, degraded: 0, warning: 0, unknown: 0 },
      severity: 'low',
      severityLabel: severityLabel('low'),
      severityTone: severityTone('low'),
      sites: [],
      topCustomers: [],
      impactedCustomerIds: [],
      mitigation: relationMitigationHint(relations),
    };
  }

  const indexes = buildIndexes({ customers, odps, olts, siteCatalog });
  const contextType = normalize(context.type || context.sourceType);
  const contextId = String(context.id || context.sourceId || '').trim();
  const customerIds = new Set();
  let directSignalCount = 0;

  const addIdSet = (ids) => {
    Array.from(ids || []).forEach((id) => {
      const token = String(id || '').trim();
      if (token) customerIds.add(token);
    });
  };

  const selectedIncident = context.incident
    || incidents.find((item) => String(item?.id || '').trim() === String(context.incidentId || '').trim())
    || null;

  if (selectedIncident) {
    const directRefs = toArray(selectedIncident.impactedEntities || selectedIncident.linkedEntityKeys)
      .filter((ref) => String(ref || '').startsWith('customer:'));
    directSignalCount += directRefs.length;
    addIdSet(collectCustomersFromIncident({ incident: selectedIncident, indexes }));
  }

  if (contextType && contextId) {
    if (contextType === 'incident' && !selectedIncident) {
      const incidentById = incidents.find((item) => String(item?.id || '').trim() === contextId);
      if (incidentById) {
        addIdSet(collectCustomersFromIncident({ incident: incidentById, indexes }));
      }
    } else {
      const nodeCustomers = collectCustomersByNode({ contextType, contextId, indexes });
      if (contextType === 'customer' && nodeCustomers.size) directSignalCount += 1;
      addIdSet(nodeCustomers);
    }
  }

  const ticketCustomerId = String(context.customerId || '').trim();
  if (ticketCustomerId) {
    customerIds.add(ticketCustomerId);
    directSignalCount += 1;
  }

  const ticketRouterId = String(context.routerId || context.nodeId || '').trim();
  if (ticketRouterId) {
    addIdSet(collectCustomersByNode({ contextType: 'router', contextId: ticketRouterId, indexes }));
  }

  const ticketSiteId = String(context.siteId || '').trim();
  if (ticketSiteId) {
    addIdSet(collectCustomersByNode({ contextType: 'site', contextId: ticketSiteId, indexes }));
  }

  const impactedCustomers = Array.from(customerIds)
    .map((id) => indexes.customersById.get(id))
    .filter(Boolean);

  if (!impactedCustomers.length) {
    const hadContext = Boolean(selectedIncident || contextType || contextId || ticketCustomerId || ticketRouterId || ticketSiteId);
    const state = hadContext ? 'partial' : 'empty';
    return {
      state,
      message: customerImpactStateMessage[state],
      counts: { total: 0, offline: 0, degraded: 0, warning: 0, unknown: 0 },
      severity: 'low',
      severityLabel: severityLabel('low'),
      severityTone: severityTone('low'),
      sites: [],
      topCustomers: [],
      impactedCustomerIds: [],
      directSignalCount,
      mitigation: relationMitigationHint(relations),
    };
  }

  const counts = { total: impactedCustomers.length, offline: 0, degraded: 0, warning: 0, unknown: 0 };
  const siteCounter = new Map();

  const detailedCustomers = impactedCustomers.map((customer) => {
    const id = customerIdOf(customer);
    const code = customerCodeOf(customer);
    const name = customerNameOf(customer);
    const status = customerStatusRaw(customer);
    const state = classifyCustomerState(status);
      const site = customerSiteOf(customer, siteCatalog);

    if (state === 'offline') counts.offline += 1;
    else if (state === 'degraded') counts.degraded += 1;
    else if (state === 'warning') counts.warning += 1;
    else if (state === 'unknown') counts.unknown += 1;

    if (site && site !== '-') siteCounter.set(site, Number(siteCounter.get(site) || 0) + 1);

    return {
      id,
      code,
      name,
      site,
      status,
      state,
    };
  });

  const stateRank = { offline: 0, degraded: 1, warning: 2, unknown: 3, healthy: 4 };
  detailedCustomers.sort((a, b) => {
    const rankDiff = (stateRank[a.state] ?? 5) - (stateRank[b.state] ?? 5);
    if (rankDiff !== 0) return rankDiff;
    return a.name.localeCompare(b.name);
  });

  const severity = deriveSeverity(counts);
  const topCustomers = detailedCustomers.slice(0, 5);
  const impactedSites = Array.from(siteCounter.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([site, total]) => ({ site, total }));

  return {
    state: 'ok',
    message: '',
    counts,
    severity,
    severityLabel: severityLabel(severity),
    severityTone: severityTone(severity),
    sites: impactedSites,
    customers: detailedCustomers,
    topCustomers,
    impactedCustomerIds: detailedCustomers.map((item) => item.id),
    directSignalCount,
    mitigation: relationMitigationHint(relations),
  };
};
